import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@panelgenie/shared-ui";
import type { InvocationRecord, ParseResult } from "../types.js";
import {
  formatCost,
  formatTokens,
  geminiBackendLabel,
  kindLabel,
  latencyBreakdown,
  outcomeBadgeVariant,
  outcomeLabel,
  serviceTierLabel,
} from "../lib/format.js";

/**
 * The detail screen's top bar (handoff §1). A back affordance, the
 * invocation's identity (id · kind · outcome · parse), and the headline
 * facts a support engineer reads *first* to frame the problem: is this a
 * model failure, a parse failure, or an image-pipeline failure? — provider
 * · model, latency, token usage, estimated cost (brief §5).
 *
 * The prototype's demo-state switcher is intentionally dropped: real state
 * derives from the invocation, not a toggle.
 */

export type InvocationTopBarProps = {
  readonly invocation: InvocationRecord;
  readonly parse: ParseResult;
  /** Right-aligned action slot (View JSON / Save as test case). */
  readonly actions?: ReactNode;
};

export function InvocationTopBar({
  invocation,
  parse,
  actions,
}: InvocationTopBarProps) {
  // Only Gemini scans carry a backend; stub/replay and pre-column rows render
  // no Backend fact (null label) rather than a blank one.
  const backendLabel = geminiBackendLabel(invocation.geminiBackend);
  // KAN-168 — tier that actually served the scan (Priority vs downgraded).
  const tierLabel = serviceTierLabel(invocation.serviceTierServed);
  return (
    <header
      aria-label="invocation-top-bar"
      className="flex h-16 flex-shrink-0 items-center gap-5 border-b border-border-1 bg-bg-1 px-5"
    >
      <Link
        to="/"
        aria-label="back-to-invocations"
        title="Back to invocations"
        className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border-1 bg-bg-3 text-fg-1 transition-colors hover:bg-bg-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <ArrowLeft />
      </Link>

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] font-semibold text-fg-1">Invocation</span>
          <span
            className="truncate font-mono text-xs text-fg-3"
            style={{ maxWidth: 240 }}
            title={invocation.id}
          >
            {invocation.id}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-sm border border-border-1 bg-bg-4 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-fg-2">
            {kindLabel(invocation.kind)}
          </span>
          <Badge variant={outcomeBadgeVariant(invocation.outcome)}>
            {outcomeLabel(invocation.outcome)}
          </Badge>
          <Badge variant={parse.status === "failed" ? "danger" : "neutral"}>
            parse: {parse.status === "ok" ? "ok" : parse.status === "failed" ? "failed" : "none"}
          </Badge>
        </div>
      </div>

      <div className="ml-auto hidden items-center gap-6 lg:flex">
        <Fact label="Provider" value={invocation.provider} />
        {backendLabel !== null ? (
          <Fact label="Backend" value={backendLabel} />
        ) : null}
        {tierLabel !== null ? <Fact label="Tier" value={tierLabel} /> : null}
        <LatencyFact
          latencyMs={invocation.latencyMs}
          backoffMs={invocation.backoffMs}
          retryCount={invocation.retryCount}
        />
        <Fact label="Tokens" value={formatTokens(invocation.usage)} />
        <Fact label="Est. cost" value={formatCost(invocation.usage, invocation.modelId)} />
        <span aria-hidden className="h-8 w-px bg-border-1" />
      </div>

      {actions !== undefined ? (
        <div className="flex flex-none items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

/**
 * The Latency fact. Mirrors the list's `LatencyCell` (KAN-65): the real
 * provider latency is the headline value and, when this invocation incurred
 * retry/backoff sleep, a muted second line breaks out the backoff time + retry
 * count. Legacy rows (no backoff recorded) and clean calls show the total only.
 */
function LatencyFact({
  latencyMs,
  backoffMs,
  retryCount,
}: {
  readonly latencyMs: number | null;
  readonly backoffMs: number | null;
  readonly retryCount: number | null;
}) {
  const { primary, backoff } = latencyBreakdown(latencyMs, backoffMs, retryCount);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.08em] text-fg-3">
        Latency
      </span>
      <span className="font-mono text-[13px] font-semibold text-fg-1">
        {primary}
      </span>
      {backoff !== null ? (
        <span className="font-mono text-[10px] text-fg-3">{backoff}</span>
      ) : null}
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.08em] text-fg-3">
        {label}
      </span>
      <span className="font-mono text-[13px] font-semibold text-fg-1">
        {value}
      </span>
    </div>
  );
}

function ArrowLeft() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
